import { useState } from 'react';
import { AxiosError } from 'axios';

// MUI
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import SaveOutlined from '@mui/icons-material/SaveOutlined';

// redux
import { useAppDispatch } from '@redux/store';
import { verifyConfirmationCode, cancelConfirmationCode } from '@redux/authReducer';

interface Props {
  open: boolean;
}

// Mã lỗi BE (docx UC3) → thông báo tiếng Việt hiển thị cho phụ huynh.
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_CONFIRMATION_CODE: 'Mã không hợp lệ, vui lòng kiểm tra lại.',
  CONFIRMATION_CODE_EXPIRED: 'Mã xác nhận đã hết hạn, vui lòng liên hệ nhà trường để cấp lại.',
  CONFIRMATION_CODE_EXHAUSTED: 'Mã đã hết lượt sử dụng, vui lòng liên hệ nhà trường để cấp lại.',
  // Bỏ TOO_MANY_ATTEMPTS: BE không còn khoá sau N lần nhập sai (tài khoản phụ huynh dùng chung
  // theo trường nên khoá theo tài khoản chặn oan cả trường) — giữ lại chỉ tạo câu thông báo về
  // một cơ chế không còn tồn tại.
  DEFAULT: 'Có lỗi xảy ra, vui lòng thử lại.',
};

/**
 * Popup "Nhập mã xác nhận" hiện NGAY trên trang đăng nhập khi phụ huynh (KH-PHOTHONG) login xong
 * nhưng chưa nhập mã. Xác nhận → verify + vào app; Quay lại → hủy, về form login. (Figma 6788-23724)
 */
const ConfirmationCodeDialog = ({ open }: Props) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    const value = code.trim();
    if (!value) {
      setError('Vui lòng nhập mã xác nhận.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await dispatch(verifyConfirmationCode(value));
      // Thành công: pendingConfirmationCode được xóa → app tự render vào Dashboard.
    } catch (e) {
      const data = (e as AxiosError<{ code?: string; Code?: string }>)?.response?.data;
      const errCode = data?.code ?? data?.Code ?? 'DEFAULT';
      setError(ERROR_MESSAGES[errCode] ?? ERROR_MESSAGES.DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setCode('');
    setError('');
    dispatch(cancelConfirmationCode());
  };

  // Màu lấy từ Figma (node 6788-23724): viền popup đỏ brand, nút cam.
  const RED = '#E41E26';
  const ORANGE = '#FF490C';

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Không cho đóng bằng click ngoài/ESC — buộc chọn Quay lại hoặc Xác nhận.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      PaperProps={{
        sx: { borderRadius: '10px', border: `1px solid ${RED}`, width: 474, maxWidth: '92vw' },
      }}
    >
      <DialogContent sx={{ p: 1.5 }}>
        <Typography sx={{ mb: 1, fontSize: 14, color: '#31211B' }}>
          Vui lòng nhập mã xác nhận:{' '}
          <Box component="span" sx={{ color: RED }}>
            *
          </Box>
        </Typography>
        <TextField
          fullWidth
          autoFocus
          size="small"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
          placeholder="Nhập mã xác nhận tại đây"
          error={!!error}
          helperText={error}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '10px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D6D6D6' },
          }}
        />
        <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackRounded />}
            onClick={handleBack}
            disabled={loading}
            sx={{
              color: ORANGE,
              borderColor: ORANGE,
              fontWeight: 700,
              borderRadius: '10px',
              '&:hover': { borderColor: ORANGE, backgroundColor: 'rgba(255,73,12,0.06)' },
            }}
          >
            Quay lại
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveOutlined />}
            onClick={handleConfirm}
            disabled={loading}
            sx={{
              backgroundColor: ORANGE,
              fontWeight: 700,
              borderRadius: '10px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#E5410A', boxShadow: 'none' },
            }}
          >
            Xác nhận
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationCodeDialog;
